/*
* PauseVideoPlayerButtonMediator.js
* Created on 18-3-2021
* @author Swen Meeuwes for &ranj
*/

import LayoutManager from "../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import ScreenNavigator from "../../../../lib/html5-software-library/src/ranj/ui/ScreenNavigator";
import AbstractMediator from "../ui/AbstractMediator";
import VideoPlayer from "./VideoPlayer";

export default class PauseVideoPlayerButtonMediator extends AbstractMediator {
    /**
     * @constructor
     * @param  {createjs.MovieClip} target
     * @param  {soma.EventDispatcher} dispatcher
     * @param  {LayoutManager} layoutManager
     * @param  {ScreenNavigator} screenNavigator
     */
    constructor(target, dispatcher, layoutManager, screenNavigator) {
        super(target, dispatcher);

        this._layoutManager = layoutManager;
        this._screenNavigator = screenNavigator;
    }

    initialize() {
        super.initialize();
    }

    destroy() {
        super.destroy();

        this.reset();

        // parent is responsible for destroying the video player, we'll just unlink our reference to it
        this._videoPlayer = null;
    }

    /**
     * Sets the parents skin of the pause button.
     * This will instantiate a nearly invisible full-canvas pause button in the parentSkin.
     * 
     * The resume button graphic can be found in the `template.html` file.
     * For controlling the styling properties of the overlay @see PausedOverlay.
     * 
     * @param  {createjs.Container} parentSkin
     */
    setParent(parentSkin) {
        this.reset(); // ensure previously created buttons are destroyed

        const width = this._screenNavigator.getScreenWidth();
        const height = this._screenNavigator.getScreenHeight();

        const pauseButtonGraphics = new createjs.Graphics()
            .beginFill("#000")
            .drawRect(0, 0, width, height);
        this._pauseButton = new createjs.Shape(pauseButtonGraphics);
        this._pauseButton.alpha = .01;

        this._handlePauseButtonBound = this._handlePauseButton.bind(this);
        this._pauseButton.addEventListener("click", this._handlePauseButtonBound);

        parentSkin.addChildAt(this._pauseButton, 0);

        this._pauseButton.regX = width * .5;
        this._pauseButton.regY = height * .5;
        this.stretchBackground(this._pauseButton, new createjs.Rectangle(0, 0, width, height));

        this._parentSkin = parentSkin;
    }

    /**
     * Cleans up instantiated elements and removes references to other skins and events without destroying the (this) mediator itself
     */    
    reset() {
        if (this._pauseButton) {
            this._pauseButton.removeEventListener("click", this._handlePauseButtonBound);
        }

        if (this._parentSkin) {
            this._parentSkin.removeChild(this._pauseButton);
        }

        this._pauseButton = null;
        this._handlePauseButtonBound = null;
        this._parentSkin = null;
    }

    /**
     * Sets which video player this pause button should control
     * @param  {VideoPlayer} videoPlayer
     */
    setVideoPlayer(videoPlayer) {
        this._videoPlayer = videoPlayer;
    }

    _handlePauseButton() {
        if (this._videoPlayer) {
            this._videoPlayer.pause();
        }
    };
}